import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CalendarView from "@/components/CalendarView";
import { Loader2, CalendarDays } from "lucide-react";

export default function ProviderCalendar() {
  const [allItems, setAllItems] = useState([]);
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState("all");
  const [selectedSpace, setSelectedSpace] = useState("all");
  const [selectedActivity, setSelectedActivity] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/provider/calendar"),
      api.get("/provider/venues"),
    ]).then(([calRes, venRes]) => {
      setAllItems(calRes.data);
      setVenues(venRes.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Build a unified list of spaces: legacy resources + new physical_spaces
  const allSpaces = useMemo(() => {
    const spaces = [];
    venues.forEach(v => {
      (v.resources || []).forEach(r => {
        spaces.push({ id: r.resource_id, name: r.name, venue_id: v.venue_id, venue_name: v.name, sport_type: r.sport_type, type: "resource", activities: [r.sport_type].filter(Boolean) });
      });
      (v.physical_spaces || []).forEach(s => {
        const activities = (s.activity_configs || []).map(a => a.activity_name || a.sport_type).filter(Boolean);
        spaces.push({ id: s.space_id, name: s.space_name, venue_id: v.venue_id, venue_name: v.name, sport_type: s.space_type, type: "physical_space", activities });
      });
    });
    return spaces;
  }, [venues]);

  // Filter spaces by selected venue
  const filteredSpaces = selectedVenue === "all"
    ? allSpaces
    : allSpaces.filter(s => s.venue_id === selectedVenue);

  // Build unique activity list from filtered spaces
  const activityOptions = useMemo(() => {
    const actSet = new Set();
    filteredSpaces.forEach(s => {
      s.activities.forEach(a => actSet.add(a));
      if (s.sport_type) actSet.add(s.sport_type);
    });
    return [...actSet].sort();
  }, [filteredSpaces]);

  // Filter spaces by activity
  const spacesAfterActivity = selectedActivity === "all"
    ? filteredSpaces
    : filteredSpaces.filter(s => s.activities.includes(selectedActivity) || s.sport_type === selectedActivity);

  const handleVenueChange = (v) => {
    setSelectedVenue(v);
    setSelectedSpace("all");
    setSelectedActivity("all");
  };

  const handleSpaceChange = (v) => {
    setSelectedSpace(v);
  };

  // Filter calendar items
  const filteredItems = allItems.filter(item => {
    if (selectedVenue !== "all") {
      const venueSpaceIds = allSpaces.filter(s => s.venue_id === selectedVenue).map(s => s.id);
      if (!venueSpaceIds.includes(item.resource_id) && item.venue !== venues.find(v => v.venue_id === selectedVenue)?.name) return false;
    }
    if (selectedSpace !== "all" && item.resource_id !== selectedSpace) return false;
    if (selectedActivity !== "all" && item.sport_type !== selectedActivity) return false;
    return true;
  });

  const handleItemClick = (item) => {
    if (item.type === "booking") navigate("/provider/bookings");
    else if (item.type === "event") navigate(`/events/${item.id}`);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6" data-testid="provider-calendar-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight flex items-center gap-3">
          <CalendarDays className="w-8 h-8 text-court-orange" />Venue Calendar
        </h1>
        <div className="flex gap-2 flex-wrap">
          {/* Venue filter */}
          <Select value={selectedVenue} onValueChange={handleVenueChange}>
            <SelectTrigger className="w-48" data-testid="venue-filter">
              <SelectValue placeholder="My Venues" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Venues</SelectItem>
              {venues.map(v => (
                <SelectItem key={v.venue_id} value={v.venue_id}>{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* All Spaces filter */}
          <Select value={selectedSpace} onValueChange={handleSpaceChange}>
            <SelectTrigger className="w-52" data-testid="space-filter">
              <SelectValue placeholder="All Spaces" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Spaces</SelectItem>
              {spacesAfterActivity.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name} — {s.venue_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Activity filter */}
          <Select value={selectedActivity} onValueChange={setSelectedActivity}>
            <SelectTrigger className="w-44" data-testid="activity-filter">
              <SelectValue placeholder="All Activities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Activities</SelectItem>
              {activityOptions.map(a => (
                <SelectItem key={a} value={a} className="capitalize">{a.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <CalendarView items={filteredItems} onItemClick={handleItemClick} title="Venue Calendar" defaultView="day" />
    </div>
  );
}
